import { useMemo, useState, useRef } from 'react';
import {
  createSkill as createSkillApiRequest,
  deleteSkill as deleteSkillApiRequest,
  editSkill as editSkillApiRequest,
  getSkillById as getSkillByIdApiRequest,
  getSkillsWithBasicData as getSkillsWithBasicDataApiRequest,
} from 'src/api/skill';
import { Skill } from 'src/models/skill';
import { SkillContext } from './skill-context-types';

interface ContextObject<T extends boolean> {
  full: T;
  skill: T extends true ? Skill : Omit<Skill, 'items'>;
}

interface SkillProviderProps {
  children: React.ReactNode;
}

const SkillProvider: React.FC<SkillProviderProps> = ({ children }) => {
  const [ctxObjs, setCtxObjs] = useState<ContextObject<boolean>[]>([]);
  const [hasLoadedBasicData, setHasLoadedBasicData] = useState(false);

  // Track which skillIds are already being fetched — prevents duplicate calls
  const fetchingIds = useRef<Set<string>>(new Set());

  const skillsWithBasicData = useMemo(
    () => ctxObjs.map((d) => d.skill as Omit<Skill, 'items'>),
    [ctxObjs]
  );

  const providerValue = useMemo(() => {
    const loadSkillsWithBasicData = async (): Promise<void> => {
    if (hasLoadedBasicData) return;
    const res = await getSkillsWithBasicDataApiRequest();
    if (res.success && res.data?.length) {
      setCtxObjs((prev) => 
        res.data!.map((s) => {
          // if we already have the full skill, keep it — don't downgrade to basic
          const existing = prev.find((d) => d.skill._id === s._id && d.full === true);
          if (existing) return existing;
          return { full: false, skill: s };
        })
      );
    }
    setHasLoadedBasicData(true);
  };

    const getSkillsWithBasicData = (): Omit<Skill, 'items'>[] =>
      ctxObjs.map((d) => d.skill as Omit<Skill, 'items'>);

    const getSkillById = (skillId: string): Skill | null => {
      // Already have the full skill
      const ctxObj = ctxObjs.find((d) => d.skill._id === skillId && d.full === true);
      if (ctxObj) return ctxObj.skill as Skill;

      // Already fetching — don't fire another request
      if (fetchingIds.current.has(skillId)) return null;

      // Start fetching
      fetchingIds.current.add(skillId);
      getSkillByIdApiRequest(skillId).then((res) => {
        if (res.success && res.data) {
          setCtxObjs((prev) => {
            const exists = prev.some((d) => d.skill._id === skillId);
            if (exists) {
              return prev.map((d) =>
                d.skill._id === skillId ? { full: true, skill: res.data! } : d
              );
            }
            return [...prev, { full: true, skill: res.data! }];
          });
        }
        fetchingIds.current.delete(skillId);
      });

      return null;
    };

    const createSkill = async (
      skill: Omit<Skill, '_id' | 'organization'>
    ): Promise<[string | null, string | null]> =>
      createSkillApiRequest(skill).then((res) => {
        if (res.success) {
          setCtxObjs((prev) => [...prev, { full: true, skill: res.data! }]);
          return [res.data!._id, null];
        }
        return [null, res.error ?? 'Error creating skill'];
      });

    const editSkill = async (
      skill: Partial<Skill> & { _id: string }
    ): Promise<string | null> => {
      const res = await editSkillApiRequest(skill);
      if (res.success) {
        setCtxObjs((prev) =>
          prev.map((d) =>
            d.skill._id === skill._id ? { full: true, skill: res.data! } : d
          )
        );
        return null;
      }
      return res.error ?? 'Error editing skill';
    };

    const deleteSkill = async (skillId: string): Promise<string | null> => {
      const res = await deleteSkillApiRequest(skillId);
      if (res.success) {
        setCtxObjs((prev) => prev.filter((d) => d.skill._id !== skillId));
        return null;
      }
      return res.error ?? 'Error deleting skill';
    };

    const setSkillCache = (skill: Skill): void => {
      setCtxObjs((prev) => {
        const exists = prev.some((d) => d.skill._id === skill._id);
        if (exists) {
          return prev.map((d) =>
            d.skill._id === skill._id ? { full: true, skill } : d
          );
        }
        return [...prev, { full: true, skill }];
      });
    };

    return {
      hasLoadedBasicData,
      loadSkillsWithBasicData,
      skillsWithBasicData,
      getSkillsWithBasicData,
      createSkill,
      editSkill,
      deleteSkill,
      getSkillById,
      setSkillCache,
    };
  }, [ctxObjs, hasLoadedBasicData, skillsWithBasicData]);

  return <SkillContext.Provider value={providerValue}>{children}</SkillContext.Provider>;
};

export default SkillProvider;